/**
 * Wave 5 — Lifecycle & Distribution route.
 *
 * `/app/lifecycle` with five tabs (Lead Magnets · Email-Sequenzen ·
 * Analytics-Setup · Marketing Loops · Launch-Pläne). Each tab is a
 * compact list + create form + detail dialog talking to
 * `/api/v1/lifecycle/*`.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Mail, Gift, BarChart3, RotateCw, Rocket, Sparkles } from "lucide-react";

const WRITE_ROLES = new Set(["owner", "admin", "manager"]);
const PUBLISH_ROLES = new Set(["owner", "admin", "manager"]);

const MAGNET_KINDS = ["calculator", "report", "checklist", "guide", "quiz", "webinar"];
const LOOP_KINDS = ["acquisition", "retention", "virality", "content", "paid", "referral", "organic"];

export default function Lifecycle() {
  const t = useT();
  return (
    <div className="space-y-4" data-testid="lifecycle-page">
      <header>
        <h1 className="text-2xl font-semibold">{t("lifecycle.title", "Lifecycle & Distribution")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("lifecycle.subtitle", "Lead-Magnete, E-Mail-Sequenzen, Analytics-Setup, Loops und Launch-Pläne.")}
        </p>
      </header>

      <Tabs defaultValue="magnets" className="space-y-4">
        <TabsList data-testid="lifecycle-tabs">
          <TabsTrigger value="magnets" data-testid="tab-magnets">
            <Gift className="mr-1 size-4" />
            {t("lifecycle.tabs.magnets", "Lead Magnets")}
          </TabsTrigger>
          <TabsTrigger value="sequences" data-testid="tab-sequences">
            <Mail className="mr-1 size-4" />
            {t("lifecycle.tabs.sequences", "E-Mail-Sequenzen")}
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <BarChart3 className="mr-1 size-4" />
            {t("lifecycle.tabs.analytics", "Analytics-Setup")}
          </TabsTrigger>
          <TabsTrigger value="loops" data-testid="tab-loops">
            <RotateCw className="mr-1 size-4" />
            {t("lifecycle.tabs.loops", "Marketing Loops")}
          </TabsTrigger>
          <TabsTrigger value="launches" data-testid="tab-launches">
            <Rocket className="mr-1 size-4" />
            {t("lifecycle.tabs.launches", "Launch-Pläne")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="magnets"><LeadMagnetsTab /></TabsContent>
        <TabsContent value="sequences"><SequencesTab /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
        <TabsContent value="loops"><LoopsTab /></TabsContent>
        <TabsContent value="launches"><LaunchesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Lead Magnets ────────────────────────────────────────────────────

function LeadMagnetsTab() {
  const t = useT();
  const role = (auth.getUser() || {}).role || "";
  const canWrite = WRITE_ROLES.has(role);
  const canPublish = PUBLISH_ROLES.has(role);
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("calculator");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/lifecycle/lead-magnets");
      setItems(r.data.items || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    if (!topic.trim()) return toast.error(t("lifecycle.magnets.topic_required", "Topic angeben."));
    setBusy(true);
    try {
      await api.post("/lifecycle/lead-magnets/generate",
                          { kind, topic, target_audience: "Eigentümer 55+", industry: "real_estate" });
      setTopic("");
      load();
      toast.success(t("lifecycle.magnets.generated", "Lead-Magnet erstellt."));
    } catch (e) {
      toast.error(t("lifecycle.magnets.gen_failed", "Erstellen fehlgeschlagen."));
    } finally { setBusy(false); }
  }

  async function publish(id) {
    try {
      await api.post(`/lifecycle/lead-magnets/${id}/publish`);
      load();
      toast.success(t("lifecycle.magnets.published", "Veröffentlicht."));
    } catch (e) { toast.error(t("lifecycle.magnets.publish_failed", "Publish fehlgeschlagen.")); }
  }

  return (
    <Card data-testid="magnets-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">{t("lifecycle.magnets.title", "Lead Magnets")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {canWrite && (
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-2">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="magnet-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MAGNET_KINDS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`magnet-kind-opt-${k}`}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input data-testid="magnet-topic" placeholder="Topic (z.B. Immobilienbewertung Bonn)"
                    value={topic} onChange={(e) => setTopic(e.target.value)} />
            <Button data-testid="magnet-generate-btn" onClick={generate} disabled={busy}>
              <Sparkles className="mr-1 size-4" />
              {busy ? t("common.working", "Arbeite…") : t("lifecycle.magnets.generate", "Generieren")}
            </Button>
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="magnets-empty">
            {t("lifecycle.magnets.empty", "Noch keine Lead-Magnete.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="magnets-list">
            {items.map((m) => (
              <li key={m.id} className="py-2 flex items-center gap-2" data-testid={`magnet-row-${m.id}`}>
                <span className="text-sm font-medium flex-1 truncate">{m.name}</span>
                <Badge variant="outline" className="text-[11px]">{m.kind}</Badge>
                <Badge className={m.status === "published"
                  ? "bg-emerald-100 text-emerald-900 text-[11px]"
                  : "bg-sky-100 text-sky-900 text-[11px]"}
                  data-testid={`magnet-status-${m.id}`}>{m.status}</Badge>
                {m.landing_page_id && (
                  <Badge variant="secondary" className="text-[11px]"
                          data-testid={`magnet-lp-${m.id}`}>+LP</Badge>
                )}
                {canPublish && m.status !== "published" && (
                  <Button size="sm" variant="outline"
                          data-testid={`magnet-publish-${m.id}`}
                          onClick={() => publish(m.id)}>
                    {t("lifecycle.magnets.publish", "Veröffentlichen")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Email Sequences ────────────────────────────────────────────────

function SequencesTab() {
  const t = useT();
  const role = (auth.getUser() || {}).role || "";
  const canWrite = WRITE_ROLES.has(role);
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("lifecycle");
  const [len, setLen] = useState(5);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [simResult, setSimResult] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/lifecycle/email-sequences");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openId) return;
    api.get(`/lifecycle/email-sequences/${openId}`).then(r => setDetail(r.data)).catch(() => {});
  }, [openId]);

  async function generate() {
    setBusy(true);
    try {
      await api.post("/lifecycle/email-sequences/generate", {
        kind, sequence_length: Number(len) || 5,
        audience: "leads", trigger: { type: "lead_created" },
        framework: "AIDA",
      });
      load();
      toast.success(t("lifecycle.sequences.generated", "Sequenz erstellt."));
    } catch (e) {
      toast.error(t("lifecycle.sequences.gen_failed", "Erstellen fehlgeschlagen."));
    } finally { setBusy(false); }
  }

  async function simulate(id) {
    const r = await api.post(`/lifecycle/email-sequences/${id}/simulate`);
    setSimResult(r.data);
  }
  async function activate(id) {
    await api.post(`/lifecycle/email-sequences/${id}/activate`);
    load();
    toast.success(t("lifecycle.sequences.activated", "Aktiviert."));
  }

  return (
    <Card data-testid="sequences-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">
        {t("lifecycle.sequences.title", "E-Mail-Sequenzen")}
      </CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {canWrite && (
          <div className="grid grid-cols-1 md:grid-cols-[160px_100px_auto] gap-2">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="seq-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lifecycle">lifecycle</SelectItem>
                <SelectItem value="cold">cold</SelectItem>
                <SelectItem value="transactional">transactional</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="seq-length" type="number" min={1} max={12}
                    value={len} onChange={(e) => setLen(e.target.value)} />
            <Button data-testid="seq-generate-btn" onClick={generate} disabled={busy}>
              {busy ? t("common.working", "Arbeite…") : t("lifecycle.sequences.generate", "Generieren")}
            </Button>
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="sequences-empty">
            {t("lifecycle.sequences.empty", "Noch keine Sequenzen.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="sequences-list">
            {items.map((s) => (
              <li key={s.id} className="py-2 flex items-center gap-2" data-testid={`seq-row-${s.id}`}>
                <span className="text-sm font-medium flex-1 truncate">{s.name}</span>
                <Badge variant="outline" className="text-[11px]">{s.kind}</Badge>
                <Badge className="text-[11px]">{s.steps?.length || 0} steps</Badge>
                <Badge className={s.status === "active"
                    ? "bg-emerald-100 text-emerald-900 text-[11px]"
                    : "bg-stone-100 text-stone-900 text-[11px]"}>{s.status}</Badge>
                <Button size="sm" variant="ghost" data-testid={`seq-open-${s.id}`}
                        onClick={() => { setOpenId(s.id); setSimResult(null); }}>
                  {t("lifecycle.sequences.open", "Öffnen")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) { setOpenId(null); setDetail(null); setSimResult(null); } }}>
        <DialogContent data-testid="seq-detail-dialog" className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle>{detail?.name || "Sequence"}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2">
              <ol className="space-y-2" data-testid="seq-steps">
                {(detail.steps || []).map((s, i) => (
                  <li key={s.id || i} className="rounded-md border p-2 space-y-1"
                      data-testid={`seq-step-${i}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline">+{s.delay_amount}{s.delay_unit?.slice(0,1)}</Badge>
                      <Badge variant="secondary">{s.framework}</Badge>
                    </div>
                    <p className="text-sm font-medium">{s.subject}</p>
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                      {(s.body_md || "").slice(0, 200)}
                    </p>
                  </li>
                ))}
              </ol>
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline"
                        data-testid="seq-simulate-btn"
                        onClick={() => simulate(detail.id)}>
                  {t("lifecycle.sequences.simulate", "Simulieren")}
                </Button>
                {canWrite && detail.status !== "active" && (
                  <Button size="sm" data-testid="seq-activate-btn"
                          onClick={() => activate(detail.id)}>
                    {t("lifecycle.sequences.activate", "Aktivieren")}
                  </Button>
                )}
              </div>
              {simResult && (
                <div className="mt-2" data-testid="seq-sim-result">
                  <p className="text-xs text-muted-foreground">
                    {simResult.would_send} {t("lifecycle.sequences.would_send", "E-Mails werden gesendet")}
                  </p>
                  <ul className="text-xs mt-1 space-y-1">
                    {(simResult.previews || []).map((p, i) => (
                      <li key={i} data-testid={`seq-sim-step-${i}`}>
                        <span className="font-mono">{p.scheduled_send_at?.slice(0, 16)}</span>
                        {" — "}<strong>{p.subject}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Analytics ──────────────────────────────────────────────────────

function AnalyticsTab() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [platforms, setPlatforms] = useState(["ga4", "meta_pixel", "gtm"]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get("/lifecycle/analytics");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  async function generate() {
    setBusy(true);
    try {
      await api.post("/lifecycle/analytics/generate",
                          { platforms, goals: ["lead", "sale"] });
      load();
      toast.success(t("lifecycle.analytics.generated", "Analytics-Setup erstellt."));
    } catch (e) {
      toast.error(t("lifecycle.analytics.gen_failed", "Erstellen fehlgeschlagen."));
    } finally { setBusy(false); }
  }
  return (
    <Card data-testid="analytics-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">
        {t("lifecycle.analytics.title", "Analytics-Setup")}
      </CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {["ga4", "meta_pixel", "gtm", "linkedin_insight", "tiktok_pixel"].map((p) => (
            <label key={p} className="flex items-center gap-1 text-xs cursor-pointer"
                    data-testid={`analytics-plat-${p}`}>
              <input type="checkbox" checked={platforms.includes(p)}
                        onChange={(e) => setPlatforms(e.target.checked
                          ? [...platforms, p]
                          : platforms.filter((x) => x !== p))} />
              <span className="font-mono">{p}</span>
            </label>
          ))}
          <Button size="sm" data-testid="analytics-generate-btn"
                  onClick={generate} disabled={busy || platforms.length === 0}>
            {busy ? t("common.working", "Arbeite…") : t("lifecycle.analytics.generate", "Generieren")}
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="analytics-empty">
            {t("lifecycle.analytics.empty", "Noch kein Setup.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="analytics-list">
            {items.map((a) => (
              <li key={a.id} className="py-2" data-testid={`analytics-row-${a.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium flex-1">{a.name}</span>
                  {(a.platforms || []).map((p) => (
                    <Badge key={p.platform} variant="outline" className="text-[11px]">
                      {p.platform}
                    </Badge>
                  ))}
                </div>
                {a.generated_snippets && (
                  <pre data-testid={`analytics-snippets-${a.id}`}
                        className="mt-1 text-[10px] bg-muted p-1 rounded font-mono max-h-24 overflow-auto">
                    {(a.generated_snippets.ga4_snippet || "").slice(0, 200)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Marketing Loops ────────────────────────────────────────────────

function LoopsTab() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("acquisition");
  const load = useCallback(async () => {
    const r = await api.get("/lifecycle/marketing-loops");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  async function generate() {
    try {
      await api.post("/lifecycle/marketing-loops/generate", { kind });
      load();
      toast.success(t("lifecycle.loops.generated", "Loop erstellt."));
    } catch (e) { toast.error(t("lifecycle.loops.gen_failed", "Fehlgeschlagen.")); }
  }
  return (
    <Card data-testid="loops-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">
        {t("lifecycle.loops.title", "Marketing Loops")}
      </CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="loop-kind" className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LOOP_KINDS.map((k) => (
                <SelectItem key={k} value={k} data-testid={`loop-kind-opt-${k}`}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="loop-generate-btn" onClick={generate}>
            {t("lifecycle.loops.generate", "Loop generieren")}
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="loops-empty">
            {t("lifecycle.loops.empty", "Noch keine Loops.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="loops-list">
            {items.map((lp) => (
              <li key={lp.id} className="py-2 space-y-1" data-testid={`loop-row-${lp.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium flex-1">{lp.name}</span>
                  <Badge variant="outline" className="text-[11px]">{lp.kind}</Badge>
                  <Badge className="text-[11px]">{lp.status}</Badge>
                </div>
                <div className="flex items-center gap-1 flex-wrap text-[11px] text-muted-foreground">
                  {(lp.tactic_steps || []).map((s, i) => (
                    <span key={i} className="rounded bg-muted px-1 font-mono">
                      {typeof s === "string" ? s : (s.step || JSON.stringify(s))}
                    </span>
                  )).reduce((acc, cur, i) => acc.length ? [...acc, <span key={`arr-${i}`}>→</span>, cur] : [cur], [])}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Launch Plans ────────────────────────────────────────────────────

function LaunchesTab() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get("/lifecycle/launch-plans");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  async function generate() {
    if (!date) return toast.error(t("lifecycle.launches.date_required", "Datum wählen."));
    setBusy(true);
    try {
      await api.post("/lifecycle/launch-plans/generate",
                          { launch_date: date, playbook_kind: "campaign_launch" });
      load();
      toast.success(t("lifecycle.launches.generated", "Launch-Plan erstellt."));
    } catch (e) { toast.error(t("lifecycle.launches.gen_failed", "Fehlgeschlagen.")); }
    finally { setBusy(false); }
  }
  async function flipTask(planId, taskId, newStatus) {
    const body = { status: newStatus };
    if (newStatus === "todo") body.reason = "manuell zurückgesetzt";
    await api.patch(`/lifecycle/launch-plans/${planId}/tasks/${taskId}`, body);
    load();
  }
  return (
    <Card data-testid="launches-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">
        {t("lifecycle.launches.title", "Launch-Pläne")}
      </CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input data-testid="launch-date" type="date"
                  value={date} onChange={(e) => setDate(e.target.value)} className="w-[180px]" />
          <Button data-testid="launch-generate-btn" onClick={generate} disabled={busy}>
            {busy ? t("common.working", "Arbeite…") : t("lifecycle.launches.generate", "Launch-Plan generieren")}
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="launches-empty">
            {t("lifecycle.launches.empty", "Noch keine Launch-Pläne.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="launches-list">
            {items.map((lp) => (
              <li key={lp.id} className="py-2 space-y-2" data-testid={`launch-row-${lp.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium flex-1">{lp.name}</span>
                  <Badge variant="outline" className="text-[11px]">{lp.launch_date}</Badge>
                  <Badge className="text-[11px]">{lp.status}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  {(lp.phases || []).map((ph) => (
                    <div key={ph.name} className="rounded-md border p-2"
                          data-testid={`launch-phase-${lp.id}-${ph.name}`}>
                      <p className="text-[11px] uppercase font-medium text-muted-foreground">{ph.name}</p>
                      <ul className="mt-1 space-y-1">
                        {(ph.tasks || []).map((task) => (
                          <li key={task.id}
                              className="flex items-center gap-1 text-xs"
                              data-testid={`launch-task-${task.id}`}>
                            <select value={task.status}
                                      onChange={(e) => flipTask(lp.id, task.id, e.target.value)}
                                      className="text-[10px] border rounded px-0.5"
                                      data-testid={`launch-task-status-${task.id}`}>
                              {["todo","doing","done","blocked"].map(st => (
                                <option key={st} value={st}>{st}</option>
                              ))}
                            </select>
                            <span className="truncate">{task.title}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
